import {
  Children,
  createContext,
  isValidElement,
  type AnchorHTMLAttributes,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

export interface AppLocation {
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
}

interface NavigateOptions {
  replace?: boolean;
  state?: unknown;
}

type Navigate = (to: string, options?: NavigateOptions) => void;

interface RouterValue {
  location: AppLocation;
  navigate: Navigate;
}

const RouterContext = createContext<RouterValue | null>(null);
const ParamsContext = createContext<Readonly<Record<string, string>>>({});

function hasAsciiControlCharacter(value: string): boolean {
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    if (codePoint !== undefined && (codePoint <= 0x1f || codePoint === 0x7f)) return true;
  }
  return false;
}

function currentLocation(): AppLocation {
  return {
    pathname: window.location.pathname || "/",
    search: window.location.search,
    hash: window.location.hash,
    state: window.history.state as unknown,
  };
}

export function safeAppPath(to: string): string | null {
  const value = to.trim();
  if (
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("\\") ||
    hasAsciiControlCharacter(value)
  ) {
    return null;
  }
  try {
    const resolved = new URL(value, window.location.origin);
    if (resolved.origin !== window.location.origin || resolved.username || resolved.password) {
      return null;
    }
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch {
    return null;
  }
}

export function BrowserRouter({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState<AppLocation>(currentLocation);
  const navigate = useCallback<Navigate>((to, options = {}) => {
    const target = safeAppPath(to);
    if (!target) return;
    if (options.replace) {
      window.history.replaceState(options.state ?? null, "", target);
    } else {
      window.history.pushState(options.state ?? null, "", target);
    }
    setLocation(currentLocation());
  }, []);

  useEffect(() => {
    const update = () => setLocation(currentLocation());
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);

  const value = useMemo(() => ({ location, navigate }), [location, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export interface RouteProps {
  path: string;
  element: ReactNode;
}

export function Route(props: RouteProps): null {
  void props;
  return null;
}

function decodedSegments(pathname: string): string[] | null {
  try {
    return pathname
      .replace(/^\/+|\/+$/gu, "")
      .split("/")
      .filter(Boolean)
      .map((segment) => decodeURIComponent(segment));
  } catch {
    return null;
  }
}

function matchPath(pattern: string, pathname: string): Readonly<Record<string, string>> | null {
  if (pattern === "*") return {};
  const routeSegments = decodedSegments(pattern);
  const pathSegments = decodedSegments(pathname);
  if (!routeSegments || !pathSegments || routeSegments.length !== pathSegments.length) {
    return null;
  }
  const params: Record<string, string> = {};
  for (let index = 0; index < routeSegments.length; index += 1) {
    const routeSegment = routeSegments[index]!;
    const pathSegment = pathSegments[index]!;
    if (routeSegment.startsWith(":")) {
      const name = routeSegment.slice(1);
      if (!name) return null;
      params[name] = pathSegment;
    } else if (routeSegment !== pathSegment) {
      return null;
    }
  }
  return params;
}

export function Routes({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  let fallback: ReactElement<RouteProps> | null = null;
  for (const child of Children.toArray(children)) {
    if (!isValidElement<RouteProps>(child) || child.type !== Route) continue;
    if (child.props.path === "*") {
      fallback = child;
      continue;
    }
    const params = matchPath(child.props.path, pathname);
    if (params) {
      return <ParamsContext.Provider value={params}>{child.props.element}</ParamsContext.Provider>;
    }
  }
  return fallback ? (
    <ParamsContext.Provider value={{}}>{fallback.props.element}</ParamsContext.Provider>
  ) : null;
}

export function Navigate({
  to,
  replace,
  state,
}: {
  to: string;
  replace?: boolean;
  state?: unknown;
}) {
  const navigate = useNavigate();
  useLayoutEffect(() => {
    navigate(to, { replace: replace ?? false, state });
  }, [navigate, replace, state, to]);
  return null;
}

export function useLocation(): AppLocation {
  const context = useContext(RouterContext);
  if (!context) throw new Error("useLocation must be used inside BrowserRouter.");
  return context.location;
}

export function useNavigate(): Navigate {
  const context = useContext(RouterContext);
  if (!context) throw new Error("useNavigate must be used inside BrowserRouter.");
  return context.navigate;
}

export function useParams<T extends string = string>(): Readonly<Partial<Record<T, string>>> {
  return useContext(ParamsContext) as Readonly<Partial<Record<T, string>>>;
}

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  to: string;
};

export function Link({ to, onClick, target, ...props }: LinkProps) {
  const navigate = useNavigate();
  const href = safeAppPath(to) ?? "/";
  const follow = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      (target && target !== "_self")
    ) {
      return;
    }
    event.preventDefault();
    navigate(href);
  };
  return <a {...props} href={href} target={target} onClick={follow} />;
}

type NavLinkProps = Omit<LinkProps, "className"> & {
  end?: boolean;
  className?: string | ((state: { isActive: boolean }) => string);
};

export function NavLink({ to, end = false, className, ...props }: NavLinkProps) {
  const location = useLocation();
  const href = safeAppPath(to) ?? "/";
  const targetPath = new URL(href, window.location.origin).pathname.replace(/\/+$/u, "") || "/";
  const currentPath = location.pathname.replace(/\/+$/u, "") || "/";
  const isActive =
    currentPath === targetPath ||
    (!end && targetPath !== "/" && currentPath.startsWith(`${targetPath}/`));
  const customClass = typeof className === "function" ? className({ isActive }) : className;
  const classes = [customClass, isActive ? "active" : ""].filter(Boolean).join(" ");
  return (
    <Link
      {...props}
      to={href}
      {...(classes ? { className: classes } : {})}
      {...(isActive ? { "aria-current": "page" as const } : {})}
    />
  );
}
